import { Component, Input, OnChanges, SimpleChanges } from '@angular/core';

import { BetType } from '@bpos/common';
import { OptionQuery } from '@frontend/sports/navigation/core/feature/pick-uri';
import { CustomerOfferService } from '@frontend/sports/new-customer-offer-feature';
import { SlipOptionType } from '@frontend/sports/types/betslip';

import { BetslipType } from '../../../core/betslip-type';
import { BetslipGroupPick } from '../../../core/picks/betslip-group-pick';
import { BetslipV1Pick } from '../../../core/picks/betslip-v1-pick';
import { BetslipV2OptionMarketPick } from '../../../core/picks/betslip-v2-option-market-pick';
import { ISucceededSlipResult } from '../../betplacement/models';
import BetslipPickOptionsService from '../services/betslip-pick-options.service';

export interface IPlacedBetInfo {
    picksParams: OptionQuery[];
    betslipId: string;
    location: string;
    betType: string;
    pickCount: number;
    eventName?: string;
    systemType?: string;
    hasEachWay: boolean;
}

@Component({
    selector: 'bs-result-share-my-bet',
    templateUrl: './result-share-my-bet.component.html',
    styleUrls: ['./result-share-my-bet.component.scss'],
    standalone: false,
})
export default class ResultShareMyBetComponent implements OnChanges {
    @Input() slip: ISucceededSlipResult;
    @Input() trackingInformation: string;

    placedBetInfo: IPlacedBetInfo;
    isSupported: boolean;

    constructor(
        private betslipPickOptionsService: BetslipPickOptionsService,
        private customerOfferService: CustomerOfferService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        this.isSupported = this.isShareMyBetSupported(this.slip);
        if (this.isSupported) {
            this.placedBetInfo = this.getPlacedBetInfo(this.slip);
        }
    }

    private getPlacedBetInfo(slip: ISucceededSlipResult): IPlacedBetInfo {
        let options: OptionQuery[] = [];

        const clientPicks = slip.picks.filter((pick) => pick.clientPick !== undefined).map((pick) => pick.clientPick!);
        const pickIds = slip.picks.filter((pick) => pick.clientPick === undefined).map((pick) => pick.pickId);

        if (clientPicks.length > 0) {
            const clientPickOptions = this.betslipPickOptionsService.convertFromPicks(clientPicks);
            options = options.concat(clientPickOptions);
        }

        if (pickIds.length > 0) {
            const pickIdOptions = this.betslipPickOptionsService.convertFromPickIds(pickIds);
            options = options.concat(pickIdOptions);
        }

        const betType = clientPicks.some((pick) => BetslipGroupPick.isPick(pick))
            ? BetslipType.Combo
            : options.length === 1
              ? BetslipType.Single
              : BetslipType.Combo;

        const info: IPlacedBetInfo = {
            picksParams: options,
            betslipId: slip.betNumber,
            location: this.trackingInformation,
            betType,
            pickCount: options.length,
            eventName: slip.picks[0] && slip.picks[0].eventName,
            hasEachWay: slip.picks.some((pick) => (pick.participantBetDetails ?? pick.optionBetDetails)?.betType === BetType.EachWay),
        };

        return info;
    }

    private isShareMyBetSupported(slip: ISucceededSlipResult): boolean {
        const picks = slip.picks;
        const sportPicks = picks.filter((p) => p.slipOptionType === SlipOptionType.Regular && p.clientPick).map((p) => p.clientPick!);

        const betbuilderPicks = picks.filter((p) => p.slipOptionType === SlipOptionType.BetBuilder);
        const virtualSportPicks = sportPicks.filter((pick) => BetslipV2OptionMarketPick.isPick(pick) && pick.isVirtual);
        const hasCustomerOfferPicks = sportPicks.some(
            (pick) => BetslipV1Pick.isPick(pick) && this.customerOfferService.isNewCustomerOffer(pick.league.id),
        );

        if (hasCustomerOfferPicks) {
            return false;
        }

        if (betbuilderPicks.length !== 0 || picks.length === virtualSportPicks.length) {
            return false;
        }

        return true;
    }
}
